import L from "leaflet";
import STATUS_COLORS from "./statusColors";

const markerIconCache = new Map();

/** Full marker hit box (CSS px). Visual pin sits inside; taps use the whole box. */
const HIT_W = 52;
const HIT_H = 58;
const PIN_W = { normal: 28, selected: 32, disabled: 28 };
const PIN_H = { normal: 38, selected: 42, disabled: 38 };

function sanitizePinColor(color) {
  const c = String(color || "").trim();
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(c)
    ? c
    : "#42A5F5";
}

function houseGlyphFill(pinColor) {
  const hex = sanitizePinColor(pinColor).slice(1);
  const full =
    hex.length === 3 ? [...hex].map((ch) => ch + ch).join("") : hex.slice(0, 6);
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.72 ? "#333333" : "#ffffff";
}

function pinSvgMarkup(color, variant) {
  const fill = sanitizePinColor(color);
  const house = houseGlyphFill(fill);
  const selected = variant === "selected";
  const disabled = variant === "disabled";
  const w = PIN_W[variant] || PIN_W.normal;
  const h = PIN_H[variant] || PIN_H.normal;
  const opacity = disabled ? "0.45" : "1";
  const filter = disabled ? "grayscale(60%)" : "none";
  const bodyStroke = selected ? 1.35 : 1.7;

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 32" width="${w}" height="${h}" aria-hidden="true" focusable="false" style="filter:${filter};opacity:${opacity}">
    <path fill="rgba(0,0,0,0.22)" d="M12 31.15c2.15 0 3.9-.55 3.9-1.25s-1.75-1.25-3.9-1.25-3.9.55-3.9 1.25 1.75 1.25 3.9 1.25z"/>
    ${
      selected
        ? `<path fill="none" stroke="#0057A4" stroke-width="3.35" stroke-linejoin="round" d="M12 1.35C6.55 1.35 2.15 5.75 2.15 11.2c0 8.15 9.85 19.25 9.85 19.25s9.85-11.1 9.85-19.25C21.85 5.75 17.45 1.35 12 1.35z"/>`
        : ""
    }
    <path fill="${fill}" stroke="#ffffff" stroke-width="${bodyStroke}" stroke-linejoin="round" d="M12 1.35C6.55 1.35 2.15 5.75 2.15 11.2c0 8.15 9.85 19.25 9.85 19.25s9.85-11.1 9.85-19.25C21.85 5.75 17.45 1.35 12 1.35z"/>
    <path fill="${house}" d="M12 6.15l-4.55 3.55v5.45h2.85v-2.8h3.4v2.8h2.85v-5.45z"/>
  </svg>`;
}

function createLeadPinIcon(variant, color) {
  const className = [
    "modern-pin",
    "tc-lead-map-pin",
    variant === "selected" ? "modern-pin-selected" : "",
    variant === "disabled" ? "modern-pin-disabled" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return L.divIcon({
    className,
    html: `<div class="tc-lead-pin-hit">${pinSvgMarkup(color, variant)}</div>`,
    iconSize: [HIT_W, HIT_H],
    iconAnchor: [HIT_W / 2, HIT_H - 3],
    popupAnchor: [0, -(HIT_H - 10)],
  });
}

export function getCachedMarkerIcon(variant, color) {
  const key = `${variant}:${color}`;
  const cached = markerIconCache.get(key);
  if (cached) return cached;
  const icon = createLeadPinIcon(variant, color);
  markerIconCache.set(key, icon);
  return icon;
}

export function getLeadMapPinColor(status = "New", notes = "") {
  const normalized = status.trim().toLowerCase();

  if (normalized === "new" && notes) {
    const notesLower = notes.toLowerCase();
    if (notesLower.includes("adt")) return "#F5D327";
    if (notesLower.includes("title company") || notesLower.includes("realtor")) {
      return "#A627F5";
    }
    if (notesLower.includes("builder relationship")) return "#F527C8";
  }

  const match = Object.entries(STATUS_COLORS).find(
    ([key]) => key.toLowerCase() === normalized
  );
  return match ? match[1] : "#42A5F5";
}
